import type {
  ExecutionContext,
  RecordSiteDocumentCommand,
  SiteDocument,
  SiteDocumentKind,
  SiteDocumentRepository,
} from "@dker/core";
import { DomainError, SITE_DOCUMENT_KINDS } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const COLUMNS =
  "id, project_id, storage_path, file_name, mime_type, size_bytes, kind, shared_with_field, created_at";

function toDocument(row: Row): SiteDocument {
  const kind = (SITE_DOCUMENT_KINDS as readonly string[]).includes(row.kind)
    ? (row.kind as SiteDocumentKind)
    : "autre";
  return {
    id: row.id,
    projectId: row.project_id,
    storagePath: row.storage_path,
    fileName: row.file_name,
    mimeType: row.mime_type,
    sizeBytes: Number(row.size_bytes),
    kind,
    sharedWithField: Boolean(row.shared_with_field),
    createdAt: row.created_at,
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

/**
 * Les documents de chantier (`project_documents`). La RLS décide de ce que
 * l'appelant voit : cet adaptateur ne filtre rien lui-même, sinon deux règles
 * diraient la même chose et finiraient par se contredire.
 */
export function createSupabaseSiteDocumentRepository(
  clientFor: DbClientFactory,
): SiteDocumentRepository {
  return {
    async listByProject(ctx: ExecutionContext, projectId: string): Promise<SiteDocument[]> {
      const { data, error } = await clientFor(ctx)
        .from("project_documents")
        .select(COLUMNS)
        .eq("project_id", projectId)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });
      if (error) fail(error);
      return (data ?? []).map(toDocument);
    },

    async record(ctx: ExecutionContext, command: RecordSiteDocumentCommand): Promise<SiteDocument> {
      // Ni `tenant_id` ni `uploaded_by` : leurs défauts viennent de la session.
      const { data, error } = await clientFor(ctx)
        .from("project_documents")
        .insert({
          project_id: command.projectId,
          storage_path: command.storagePath,
          file_name: command.fileName,
          mime_type: command.mimeType,
          size_bytes: command.sizeBytes,
          kind: command.kind,
        })
        .select(COLUMNS)
        .single();
      if (error) fail(error);
      return toDocument(data);
    },

    async setShared(ctx: ExecutionContext, documentId: string, shared: boolean): Promise<void> {
      const { data, error } = await clientFor(ctx)
        .from("project_documents")
        .update({ shared_with_field: shared })
        .eq("id", documentId)
        .is("deleted_at", null)
        .select("id");
      if (error) fail(error);
      // La RLS filtre une mise à jour en silence : zéro ligne veut dire refusé.
      if (!data?.length) throw new DomainError("not_found", "Ce document n'existe plus ou ne vous est pas ouvert.");
    },

    async softDelete(ctx: ExecutionContext, documentId: string): Promise<void> {
      // Par une fonction : PostgREST relit la ligne qu'il modifie, et une
      // ligne retirée n'est plus lisible (`20261029120000`).
      const { error } = await clientFor(ctx).rpc("remove_project_document", {
        p_document_id: documentId,
      });
      if (error) fail(error);
    },
  };
}
